import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllActuaciones, getAllCollections } from '../utils/actuacionesReformaApi';

function groupByTipoReforma(actuaciones) {
  return actuaciones.reduce((acc, act) => {
    const tipo = act.tipo_reforma_id;
    if (!acc[tipo]) acc[tipo] = [];
    acc[tipo].push(act);
    return acc;
  }, {});
}

const ActuacionesListPage = ({ user }) => {
  const [collections, setCollections] = useState([]);
  const [actuacionesByCollection, setActuacionesByCollection] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getAllCollections()
      .then(async (data) => {
        setCollections(data);
        // Fetch actuaciones for each collection
        const actuacionesMap = {};
        for (const col of data) {
          try {
            const acts = await getAllActuaciones(col.id);
            actuacionesMap[col.id] = acts;
          } catch (e) {
            actuacionesMap[col.id] = [];
          }
        }
        setActuacionesByCollection(actuacionesMap);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  const handleToggleRows = (colId, tipoId) => {
    setExpandedRows((prev) => ({ ...prev, [`${colId}_${tipoId}`]: !prev[`${colId}_${tipoId}`] }));
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Modelos de costes de Reforma</h2>
      </div>
      {loading && <div>Cargando...</div>}
      {error && <div className="text-red-500">Error: {error.message}</div>}
      {!loading && collections.length === 0 && <div>No hay modelos de costes de reforma registrados.</div>}
      {collections.map((col) => {
        const actuaciones = actuacionesByCollection[col.id] || [];
        if (!actuaciones.length) return null;
        const grouped = groupByTipoReforma(actuaciones);
        const isPublicBase = col.nombre?.toLowerCase().includes('base reforma');
        return (
          <div key={col.id} className="mb-12">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-bold text-blue-800">{col.nombre}</h3>
              <div className="flex gap-2">
                {isPublicBase && (
                  <button
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    onClick={() => navigate(`/colecciones/clonar/${col.id}`)}
                  >
                    Clonar
                  </button>
                )}
                {!isPublicBase && (
                  <button
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    onClick={() => navigate(`/colecciones/editar/${col.id}`)}
                  >
                    Editar
                  </button>
                )}
              </div>
            </div>
            {col.descripcion && <div className="mb-4 text-gray-600">{col.descripcion}</div>}
            {Object.entries(grouped).map(([tipoId, acts]) => {
              const key = `${col.id}_${tipoId}`;
              const showAll = !!expandedRows[key];
              const visibleActs = showAll ? acts : acts.slice(0, 3);
              const showFade = !showAll && acts.length > 3;
              return (
                <div key={tipoId} className="mb-8">
                  <h4 className="text-lg font-semibold mb-2 text-blue-700">{acts[0]?.tipo_reforma_label || `Tipo reforma ${tipoId}`}</h4>
                  <div className="bg-white shadow-md rounded-lg overflow-x-auto max-w-full mx-auto relative">
                    <table className="min-w-full divide-y divide-gray-200">
                      <colgroup>
                        <col style={{width: '160px'}} />
                        <col style={{width: '260px'}} />
                        <col style={{width: '120px'}} />
                        <col style={{width: '120px'}} />
                        <col style={{width: '120px'}} />
                        <col style={{width: '120px'}} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Nombre</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Descripción</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Coste Material</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Horas Mano Obra</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Unidad</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {visibleActs.map(act => (
                          <tr key={act.id} className="hover:bg-blue-50 transition">
                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900" title={act.nombre}>{act.nombre.length > 22 ? act.nombre.slice(0, 22) + '…' : act.nombre}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-700" title={act.descripcion}>{act.descripcion.length > 30 ? act.descripcion.slice(0, 30) + '…' : act.descripcion}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{act.coste_material}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{act.mano_obra_horas}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{act.unidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {showFade && (
                      <div className="pointer-events-none absolute left-0 right-0 bottom-10 h-12 bg-gradient-to-b from-transparent to-gray-200" />
                    )}
                    {acts.length > 3 && (
                      <div className="flex justify-center py-2 relative z-10">
                        <button
                          className="text-blue-600 hover:underline text-sm font-medium"
                          onClick={() => handleToggleRows(col.id, tipoId)}
                        >
                          {showAll ? 'Ver menos' : 'Ver más'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ActuacionesListPage; 